import * as React from "react";
import { ClickedDeviceInfo } from "../models/clickedDeviceInfo";
import { ConfigurationType } from "../models/ConfigurationType";
import { DeviceBarDevices } from "../models/DeviceBarDevices";
import { DeviceMenuProps } from "../models/deviceMenuProps";
import { DM_DataSheet } from "../models/DM_DataSheet";
import { LSM_LogicSolverSheet } from "../models/LSM_LogicSolverSheet";
import { PSF_ProtectiveFunctionComments } from "../models/PSF_ProtectiveFunctionComments";
import { SS_SupportSystemSheet } from "../models/SS_SupportSystemSheet";
import { CustomerDeviceModal } from "./CustomerDeviceModal";
import { CustomerLogicModal } from "./CustomerLogicModal";
import { CustomerSupportModal } from "./CustomerSupportModal";
import CustomerDeviceGreenRightClickMenuItems from "./CutomerDeviceGreenRightClickMenuItems";
import DeviceBarDeviceMenuItems from "./DeviceBarDeviceMenuItems";
import { DeviceModal } from "./DeviceModal";
import DeviceSubGroup from "./DeviceSubGroup";
import { ExpandedListItem } from "./ExpandableListItem";
import ContextMenu from "./ContextMenu";
import { LogicModal } from "./LogicModal";
import { DiagramApiController } from "../models/DiagramApiController.generated";
import { SrsModal } from "./SrsModal";
import ErrorAndWarningModal from "./WarningModal";
import { Message } from "../models/ProtectiveFunctionWrapper";
import { SilModal } from "./SilModal";
import { SupportModal } from "./SupportModal";
import { DeviceSheetTypes } from "../models/deviceSheeTypes";
import ConfirmDeletingCustomDataSheetModal from "./ConfirmDeletingCustomDataSheetModal";
import { UserDetails } from "../models/UserDetails";
import { DM_DataSheet_Bar } from "../models/DM_DataSheet_Bar";
import { LSM_LogicSolverSheet_Bar } from "../models/LSM_LogicSolverSheet_Bar";
import { SS_SupportSystemSheet_Bar } from "../models/SS_SupportSystemSheet_Bar";

type level1Menus =
  | "srs"
  | "logic-solver"
  | "input-configuration"
  | "input-device"
  | "action-configuration"
  | "action-device"
  | "support-configuration"
  | "support-system"
  | "custom-device"
  | "manual-device"
  | null;

type Modals =
  | "Context Menu"
  | "Logic Solver Modal"
  | "Device Modal"
  | "Support System Modal"
  | "Custom Device Modal" 
  | "Custom Logic Solver Modal"
  | "Custom Support System Modal"
  | "deviceModal"
  | "RightClick"
  | "deviceBarDeviceMenuItems"
  | "ConfirmDeletingCustomDataSheet"
  | null;

interface DeviceBarItem {
  id: string;
  label: string;
}

interface Props {
  devices: DeviceBarDevices;
  handleProcessHazardUpdate: (process_hazard: string) => void;
  onConfigurationClick: (
    configClass: string,
    configType: ConfigurationType
  ) => void;
  onSupportConfigurationClick: () => void;
  onDeviceClick: (deviceId: ClickedDeviceInfo) => void;
  selectedDevice: ClickedDeviceInfo;
  createManualInput: () => void;
  createManualAction: () => void;
  deleteCustomLogic: (lsm_logicSheet: LSM_LogicSolverSheet) => void;
  deleteCustomDevice: (dm_dataSheet: DM_DataSheet) => void;
  deleteCustomSupport: (ss_supportSystemSheet: SS_SupportSystemSheet) => void;
  saveCustomLogicSolver: (
    lsm_LogicSolverSheet: LSM_LogicSolverSheet,
    file: File
  ) => void;
  saveCustomDeviceSheet: (dm_dataSheet: DM_DataSheet, file: File) => void;
  saveCustomSupportSystem: (
    ss_supportSystemSheet: SS_SupportSystemSheet,
    file: File
  ) => void;
  saveSrsNotes: (notes: string, srsType: string) => void;
  psf_ProtectiveFunctionComments: PSF_ProtectiveFunctionComments;
  customDeviceUsageInfo: {
    function: string,
    deviceType: "device" | "logic solver" | "support system"
  };
  wipeCustomDevicePt_ProtectiveFunction: () => void;
  promoteToClientLevel: (deviceSheetTypes: DeviceSheetTypes, sheetId: number) => Promise<void>;
  userDetails: UserDetails;
}

interface State {
  level1Shown: level1Menus;
  shownModal: Modals;
  rightClickedDevice?: DM_DataSheet_Bar | null;
  rightClickedLogicDevice?: LSM_LogicSolverSheet_Bar | null;
  rightClickedSupportDevice?: SS_SupportSystemSheet_Bar | null;
  customerDeviceRightClickMenuX: number;
  customerDeviceRightClickMenuY: number;
  deviceBarDeviceMenuProps?: DeviceMenuProps;
  deviceModalProps?: DM_DataSheet;
  contextMenuProps?: DeviceMenuProps;
  logicSolverSearchPattern: string;
  inputDeviceSearchPattern: string;
  actionDeviceSearchPattern: string;
  supportSearchPattern: string;
  customSearchPattern: string;
  searchLevel1Shown: level1Menus[];
  devices: DeviceBarDevices;
  logicSolverDevices: Array<DeviceBarItem>;
  supportSystemDevices: Array<{ groupName: string; devices: Array<DeviceBarItem> }>;
  confirmDeletingCustomDataSheetTypeName?: string;
}

const logicSolverDevices: Array<DeviceBarItem> = [
  { id: "DMDIO", label: "Generic 2004D Dual MP, Dual I/O" },
  { id: "DMSIO", label: "Generic 1002D Dual MP, SIMPLEX I/O" },
  { id: "NSDD", label: "NON SC PES DUAL MP, DUAL I/O" },
  { id: "NSDS", label: "NON SC PES DUAL MP, SIMPLEX IO" },
  { id: "NSSS", label: "NON SC PES SIMPLEX MP, SIMPLEX" },
  { id: "RELFCL", label: "RELAY - FAIL TO CLOSE" },
  { id: "RELFOL", label: "RELAY - FAIL TO OPEN" },
  { id: "RELTDL", label: "RELAY - TIME DELAY" },
  { id: "SCGPE", label: "SAFETY CONFIG GP PLC" },
  { id: "SMSIO", label: "GENERIC 1OO1D SIMPLEX MP; SIMPLEX I/O" },
  { id: "TAMPNL", label: "TRIP AMPLIFIER (NON-PROGRAMMABLE)" },
  { id: "TAMPPL", label: "TRIP AMPLIFIER (PROGRAMMABLE)" },
  { id: "TMR", label: "GENERIC TMR LOGIC SOLVER" }
];

const supportSystemDevices = [
  {
    groupName: "Instrument air",
    devices: [
      { id: "IADS", label: "INSTRUMENT AIR-COMPRESSOR DIVERSITY" },
      { id: "IADRS", label: "INSTRUMENT AIR-DIVERSITY/RECEIVER" },
      {
        id: "IADRMS",
        label: "INSTRUMENT AIR-DIVERSITY/MONITORED RECEIVER"
      },
      { id: "IANDVS", label: "INSTRUMENT AIR-NO DIVERSITY" },
      { id: "IAWRCS", label: "INSTRUMENT AIR-RECEIVER" },
      { id: "IAWRMS", label: "INSTRUMENT AIR-MONITORED RECEIVER" }
    ]
  },
  {
    groupName: "Power Supply",
    devices: [
      { id: "UPSDMS", label: "UPS - POWER SUPPLY - DTT - MONITORED" },
      { id: "UPSDTS", label: "POWER SUPPLY - DTT" },
      { id: "UPSE1S", label: "POWER SUPPLY - ETT - 1 YEAR TESTING" },
      { id: "UPSE2S", label: "POWER SUPPLY - ETT - 2 YEAR TESTING" },
      { id: "UPSE3S", label: "POWER SUPPLY - ETT - 3 YEAR TESTING" },
      { id: "UPSE4S", label: "POWER SUPPLY - ETT - 4 YEAR TESTING" },
      { id: "UPSE5S", label: "POWER SUPPLY - ETT - 5 YEAR TESTING" },
      { id: "UPSE6S", label: "POWER SUPPLY - ETT - 6 YEAR TESTING" },
      { id: "UPSE7S", label: "POWER SUPPLY - ETT - 7 YEAR TESTING" },
      { id: "UPSEBS", label: "POWER SUPPLY - ETT - 6 MONTH TESTING" },
      { id: "UPSEMS", label: "POWER SUPPLY - ETT - 1 MONTH TESTING" },
      { id: "UPSEQS", label: "POWER SUPPLY - ETT - 3 MONTH TESTING" },
      { id: "UPSEWS", label: "POWER SUPPLY - ETT - WEEKLY TESTING" }
    ]
  }
];

export class DeviceBar extends React.Component<Props, State> {
  private searchInputRef: React.RefObject<HTMLInputElement>;
  private searchActionRef: React.RefObject<HTMLInputElement>;
  private searchCustomRef: React.RefObject<HTMLInputElement>;
  private searchSupportRef: React.RefObject<HTMLInputElement>;
  private searchLogicRef: React.RefObject<HTMLInputElement>;
  constructor(props: any) {
    super(props);
    this.searchInputRef = React.createRef();
    this.searchActionRef = React.createRef();
    this.searchCustomRef = React.createRef();
    this.searchSupportRef = React.createRef();
    this.searchLogicRef = React.createRef();

    this.state = {
      level1Shown: "srs",
      shownModal: null,
      rightClickedDevice: null,
      rightClickedLogicDevice: null,
      rightClickedSupportDevice: null,
      customerDeviceRightClickMenuX: null,
      customerDeviceRightClickMenuY: null,
      deviceBarDeviceMenuProps: null,
      deviceModalProps: null,
      logicSolverSearchPattern: null,
      inputDeviceSearchPattern: null,
      actionDeviceSearchPattern: null,
      supportSearchPattern: null,
      customSearchPattern: null,
      devices: new DeviceBarDevices(),
      searchLevel1Shown: [],
      logicSolverDevices: logicSolverDevices,
      supportSystemDevices: supportSystemDevices
    };

    this.closeModal = this.closeModal.bind(this);
  }

  handleLevelOneClick(e: React.MouseEvent, levelClicked: level1Menus) {
    this.handleDeviceBarClear();
    this.setState({
      level1Shown: this.state.level1Shown === levelClicked ? null : levelClicked,
    });
  }

  getLevel1Style(menuItem: string) {
    return {
      display: menuItem === this.state.level1Shown || this.state.searchLevel1Shown.indexOf(menuItem as level1Menus) > -1 ? "block" : "none"
    } as React.CSSProperties;
  }

  private handleClick = (type: string) => {
    var state = {
      shownModal: type
    } as any;
    this.setState(state);

    console.log(state);
  };

  // adds the clicked non-custom logic solver to the device register
  handleDeviceClicked = (
    deviceId: string,
    deviceType: "DEVICE" | "LS" | "SS",
    customDeviceSheetId?: number
  ) => {
    const deviceInfo = new ClickedDeviceInfo(deviceId);

    //the server expects a 0 for non-custom devices;
    switch (deviceType) {
      case "DEVICE":
        deviceInfo.deviceDataSheetId = customDeviceSheetId || 0;
        break;
      case "LS":
        deviceInfo.logicSolverSheetId = customDeviceSheetId || 0;
        break;
      case "SS":
        deviceInfo.supportSheetId = customDeviceSheetId || 0;
        break;
    }

    this.props.onDeviceClick(deviceInfo);
  };

  // Right-click device bar device and bring up context menu with open option
  handleDeviceRightClicked = (e: React.MouseEvent, dataSheet: DM_DataSheet) => {
    e.preventDefault();

    const openDeviceAction = () => {
      this.setState({
        shownModal: "deviceModal",
        deviceModalProps: dataSheet
      });
    };

    var state = {
      shownModal: "deviceBarDeviceMenuItems",
      deviceBarDeviceMenuProps: {
        top: e.clientY,
        left: e.clientX,
        actions: [{ name: "Open", do: () => openDeviceAction() }]
      }
    } as any;

    this.setState(state);
  };

  deviceRightClick = (e: React.MouseEvent, deviceType: "DEVICE" | "LS" | "SS", deviceId: string) => {
    e.preventDefault();

    const openAction = () => {
      if (deviceType === "DEVICE") {
        DiagramApiController.openDeviceSheet(deviceId)
          .then(dm_datasheet => {
            this.setState({
              rightClickedDevice: dm_datasheet,
              shownModal: "Device Modal"
            });
          });
      } else if (deviceType == "LS") {
        DiagramApiController.openLogicSolver(deviceId)
          .then(ls_dataSheet => {
            this.setState({
              rightClickedLogicDevice: ls_dataSheet,
              shownModal: "Logic Solver Modal"
            });
          });
      } else if (deviceType == "SS") {
        DiagramApiController.openSupportSheet(deviceId)
          .then(ss_supportSystemSheet => {
            this.setState({
              rightClickedSupportDevice: ss_supportSystemSheet,
              shownModal: "Support System Modal"
            });
          });
      }

      this.closeModal();
    }

    const actions = [
      { name: "Open", do: openAction },
    ];

    this.setState({
      shownModal: "Context Menu",
      contextMenuProps: {
        top: e.clientY,
        left: e.clientX,
        actions
      }
    });
  }

  // brings up the customer device right-click menu items
  handleCustomerDeviceRightClick = (
    e: React.MouseEvent,
    type: string,
    dataSheet: DM_DataSheet_Bar
  ) => {
    e.preventDefault();
    var state = {
      shownModal: type,
      rightClickedDevice: dataSheet,
      rightClickedLogicDevice: null,
      rightClickedSupportDevice: null,
      customerDeviceRightClickMenuX: e.clientX,
      customerDeviceRightClickMenuY: e.clientY
    } as Pick<State, keyof State>;
    console.log(state);
    this.setState(state);
  };

  // brings up the customer logic device right-click menu items
  handleCustomerLogicDeviceRightClick = (
    e: React.MouseEvent,
    type: string,
    dataSheet: LSM_LogicSolverSheet_Bar
  ) => {
    e.preventDefault();
    var state = {
      shownModal: type,
      rightClickedDevice: null,
      rightClickedLogicDevice: dataSheet,
      rightClickedSupportDevice: null,
      customerDeviceRightClickMenuX: e.clientX,
      customerDeviceRightClickMenuY: e.clientY
    } as Pick<State, keyof State>;
    console.log(state);
    this.setState(state);
  };

  // brings up the customer support device right-click menu items
  handleCustomerSupportDeviceRightClick = (
    e: React.MouseEvent,
    type: string,
    dataSheet: SS_SupportSystemSheet_Bar
  ) => {
    e.preventDefault();
    const state = {
      shownModal: type,
      rightClickedDevice: null,
      rightClickedLogicDevice: null,
      rightClickedSupportDevice: dataSheet,
      customerDeviceRightClickMenuX: e.clientX,
      customerDeviceRightClickMenuY: e.clientY
    } as Pick<State, keyof State>;

    this.setState(state);
  };

  handleSearch = (e: React.ChangeEvent<any>, which: "Logic" | "Input" | "Action" | "Support" | "Custom") => {
    const value = e.target.value as string;
    const searchPattern = value.toLowerCase().trim();

    const devices = { ...this.props.devices }
    let logicSolvers = [...logicSolverDevices];
    let supportDevices = [...supportSystemDevices];

    let logicSolverSearchPattern = this.state.logicSolverSearchPattern;
    let inputDeviceSearchPattern = this.state.inputDeviceSearchPattern;
    let actionDeviceSearchPattern = this.state.actionDeviceSearchPattern;
    let supportSearchPattern = this.state.supportSearchPattern;
    let customSearchPattern = this.state.customSearchPattern;

    switch (which) {
      case "Logic": {
        logicSolvers = logicSolvers.filter(ls => ls.id.toLowerCase().indexOf(searchPattern) !== -1 ||
          ls.label.toLowerCase().includes(searchPattern));
        logicSolverSearchPattern = searchPattern === "" ? null : searchPattern;
        break;
      } case "Input": {
        devices.inputDevices = devices.inputDevices.filter(x => x.deviceId.toLowerCase().indexOf(searchPattern) !== -1 ||
          x.deviceDescription.toLowerCase().includes(searchPattern));

        inputDeviceSearchPattern = searchPattern === "" ? null : searchPattern;
        break;
      } case "Action": {
        devices.actionDevices = devices.actionDevices.filter(x => x.deviceId.toLowerCase().indexOf(searchPattern) !== -1 ||
          x.deviceDescription.toLowerCase().includes(searchPattern));

        actionDeviceSearchPattern = searchPattern === "" ? null : searchPattern;
        break;
      } case "Support": {
        // not performant I know... please absolve me of my sins
        supportDevices = [];
        for (let i = 0; i < supportSystemDevices.length; i++) {
          const group = supportSystemDevices[i];
          const matches = [];
          for (let j = 0; j < group.devices.length; j++) {
            const supportDevice = group.devices[j];
            if (supportDevice.id.toLowerCase().indexOf(searchPattern) !== -1 ||
              supportDevice.label.toLowerCase().includes(searchPattern)) {
              matches.push(supportDevice);
            }
          }

          if (matches.length > 0) {
            supportDevices.push({ groupName: group.groupName, devices: matches });
          }
        }

        supportSearchPattern = searchPattern === "" ? null : searchPattern;
        break;
      } case "Custom": {
        devices.customLogicSolvers = devices.customLogicSolvers.filter(cls => cls.logicSolverSheet.deviceID.toLowerCase().indexOf(searchPattern) !== -1 ||
          cls.logicSolverSheet.deviceType.toLowerCase().includes(searchPattern));
        devices.customDevices = devices.customDevices.filter(cd => cd.dataSheet.deviceId.toLowerCase().indexOf(searchPattern) !== -1 ||
          cd.dataSheet.deviceDescription.toLowerCase().includes(searchPattern));
        devices.customSupportSystems = devices.customSupportSystems.filter(css => css.supportSystemSheet.supportSystemID.toLowerCase().indexOf(searchPattern) !== -1 ||
          css.supportSystemSheet.supportSystemType.toLowerCase().includes(searchPattern));

        customSearchPattern = searchPattern === "" ? null : searchPattern;
      }
    }

    this.setState({
      logicSolverSearchPattern,
      inputDeviceSearchPattern,
      actionDeviceSearchPattern,
      supportSearchPattern,
      customSearchPattern,
      logicSolverDevices: logicSolvers,
      devices,
      supportSystemDevices: supportDevices
    });
  }

  handleDeviceBarClear = () => {
    this.searchLogicRef.current.value = null;
    this.searchInputRef.current.value = null;
    this.searchActionRef.current.value = null;
    this.searchSupportRef.current.value = null;
    this.searchCustomRef.current.value = null;

    this.setState({
      logicSolverSearchPattern: null,
      inputDeviceSearchPattern: null, 
      actionDeviceSearchPattern: null,
      supportSearchPattern: null,
      customSearchPattern: null,
      devices: this.props.devices,
      logicSolverDevices: logicSolverDevices,
      supportSystemDevices: supportSystemDevices
    });
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.devices !== nextProps.devices) {
      this.setState({ devices: nextProps.devices });
    }
  }

  // open customer device modal, customer logic device modal, or customer support device modal depending on this.state.rightClickedDevice
  openCustomerDeviceModal = () => {
    if (this.state.rightClickedDevice != null) {
      this.setState({
        shownModal: "Custom Device Modal"
      });
    } else if (this.state.rightClickedLogicDevice != null) {
      this.setState({
        shownModal: "Custom Logic Solver Modal"
      });
    } else if (this.state.rightClickedSupportDevice != null) {
      this.setState({
        shownModal: "Custom Support System Modal"
      });
    }
  };

  openConfirmDeletingCustomDataSheetModel = () => {
    if (this.state.rightClickedDevice != null) {
      this.setState({
        confirmDeletingCustomDataSheetTypeName: "Device",
        shownModal: "ConfirmDeletingCustomDataSheet"
      });
    } else if (this.state.rightClickedLogicDevice != null) {
      this.setState({
        confirmDeletingCustomDataSheetTypeName: "Logic Solver",
        shownModal: "ConfirmDeletingCustomDataSheet"
      });
    } else if (this.state.rightClickedSupportDevice != null) {
      this.setState({
        confirmDeletingCustomDataSheetTypeName: "Support System",
        shownModal: "ConfirmDeletingCustomDataSheet"
      });
    }
  };

  confirmDeletingCustomDataSheetModelonSubmit = () => {
    if (this.state.rightClickedDevice != null) {
      this.deleteCustomDevice(this.state.rightClickedDevice.dataSheet);
    } else if (this.state.rightClickedLogicDevice != null) {
      this.deleteCustomLogic(this.state.rightClickedLogicDevice.logicSolverSheet);
    } else if (this.state.rightClickedSupportDevice != null) {
      this.deleteCustomSupport(this.state.rightClickedSupportDevice.supportSystemSheet);
    }
  };

  // delete customer logic device
  deleteCustomLogic = (lsm_logicSheet: LSM_LogicSolverSheet) => {
    this.props.deleteCustomLogic(lsm_logicSheet);
    this.closeModal();
  };

  // delete customer device
  deleteCustomDevice = (dm_dataSheet: DM_DataSheet) => {
    this.props.deleteCustomDevice(dm_dataSheet);
    this.closeModal();
  };

  // delete customer support device
  deleteCustomSupport = (ss_supportSystemSheet: SS_SupportSystemSheet) => {
    this.props.deleteCustomSupport(ss_supportSystemSheet);
    this.closeModal();
  };

  promoteToClientLevel = (deviceType: DeviceSheetTypes, sheetId: number) => {
    this.props.promoteToClientLevel(deviceType, sheetId)
      .then(() => this.closeModal())
  }

  closeModal() {
    this.setState({
      shownModal: null,
      rightClickedDevice: null,
      rightClickedLogicDevice: null,
      rightClickedSupportDevice: null,
      customerDeviceRightClickMenuX: null,
      customerDeviceRightClickMenuY: null
    });
    this.props.wipeCustomDevicePt_ProtectiveFunction();
  }

  renderSubGroup(
    inputDevices: { [subGroup: string]: DM_DataSheet[] },
    type: "Input" | "Action",
    text: string,
    lookupString?: string,
    ulClassName?: string,
    liClassName?: string
  ) {
    if (liClassName == null) {
      liClassName = text
        .toLowerCase()
        .replace(" ", "")
        .replace(/\//g, "")
        .trim();
    }
    if (ulClassName == null) {
      ulClassName = liClassName + "device";
    }
    if (lookupString == null) {
      lookupString = text;
    }    
    var device = inputDevices[lookupString] || [];   
    if (!device.length) {
      ulClassName += " hidden";
      liClassName += " hidden";
    }
    return (
      <DeviceSubGroup
        text={text}
        devices={device}
        liClassName={liClassName}
        ulClassName={ulClassName}
        type={type}
        onDeviceClick={this.handleDeviceClicked}
        onDeviceRightClick={this.deviceRightClick}
        selectedDevice={this.props.selectedDevice}
        searchPattern={type === 'Input' ? this.state.inputDeviceSearchPattern : this.state.actionDeviceSearchPattern}
      />
    );
  }

  renderInputDevices(inputDevices: { [subGroup: string]: DM_DataSheet[] }) {
    const renderInputSubGroup = text => {
      return this.renderSubGroup(inputDevices, "Input", text);
    };
    return (
      <div style={this.getLevel1Style("input-device")}>
        <ul className="input-device">
          <li>
            <input type="text" placeholder="Search" className="device-bar-search-input" ref={this.searchInputRef} onKeyUp={e => this.handleSearch(e, "Input")} />
            <input type="button" className="device-bar-search-button" value="Clear" onClick={e => this.handleDeviceBarClear()} />
          </li>
        </ul>
        {renderInputSubGroup("Trip Amplifier")}
        {renderInputSubGroup("Analyzer")}
        {renderInputSubGroup("Pressure")}
        {renderInputSubGroup("Temperature")}
        {renderInputSubGroup("Level")}
        {renderInputSubGroup("Flow")}
        {renderInputSubGroup("Flame")}
        {renderInputSubGroup("Monitor")}
        {renderInputSubGroup("Position")}
        {renderInputSubGroup("Current")}
        {renderInputSubGroup("Density")}
        {renderInputSubGroup("Hydrocarbon")}
        {renderInputSubGroup("Relay")}
        {renderInputSubGroup("Other")}
      </div>
    );
  }

  renderActionDevices(actionDevices: { [subGroup: string]: DM_DataSheet[] }) {
    const renderActionSubGroup = text => {
      return this.renderSubGroup(actionDevices, "Action", text);
    };
    return (
      <div style={this.getLevel1Style("action-device")}>
        <ul className="action-device">
          <li>
            <input type="text" placeholder="Search" className="device-bar-search-input" ref={this.searchActionRef} onKeyUp={e => this.handleSearch(e, "Action")} />
            <input type="button" className="device-bar-search-button" value="Clear" onClick={e => this.handleDeviceBarClear()} />
          </li>
        </ul>
        {renderActionSubGroup("Block Valve")}
        {renderActionSubGroup("Comp/Pump/Motor")}
        {renderActionSubGroup("Control Valve")}
        {renderActionSubGroup("Hydraulic Valve")}
        {renderActionSubGroup("MOV")}
        {renderActionSubGroup("Positioner")}
        {renderActionSubGroup("RCS package")}
        {renderActionSubGroup("Rising Stem Valve")}
        {renderActionSubGroup("Relay")}
        {renderActionSubGroup("Solenoid Valve")}
        {renderActionSubGroup("Instrument Air")}
        {renderActionSubGroup("Trip Amplifier")}
        {renderActionSubGroup("Power")}
        {renderActionSubGroup("Other")}
      </div>
    );
  }

  isNonCustomDeviceSelected() {
    return (
      this.props.selectedDevice &&
      !this.props.selectedDevice.deviceDataSheetId &&
      !this.props.selectedDevice.logicSolverSheetId &&
      !this.props.selectedDevice.supportSheetId
    );
  }

  isCustomDataSheetAllowed() {
    if (this.props.userDetails == null || this.props.userDetails.profile == null) {
      return false;
    }
    return this.props.userDetails.profile.createCustomDatasheet;
  }

  renderLogicSolvers() {
    return (
      <ul className="logicsolver" style={this.getLevel1Style("logic-solver")}>
        <li style={this.getLevel1Style("logic-solver")}>
          <input type="text" placeholder="Search" className="device-bar-search-input" ref={this.searchLogicRef} onKeyUp={e => this.handleSearch(e, "Logic")} />
          <input type="button" className="device-bar-search-button" value="Clear" onClick={e => this.handleDeviceBarClear()} />
        </li>
        {this.state.logicSolverDevices.map((device, i) => {
          const selectedClassName =
            this.isNonCustomDeviceSelected() &&
            device.id === this.props.selectedDevice.deviceId
              ? "selected-device"
              : "";
          return (
            <li
              id={device.id}
              key={i}
              className={selectedClassName}
              onClick={_ => this.handleDeviceClicked(device.id, "LS")}
              onContextMenu={e => this.deviceRightClick(e, "LS", device.id)}
              draggable
              onDragStart={() => this.handleDeviceClicked(device.id, "LS")}
            >
              <div>
                <img src={`/HTMLImages/LSS/${device.id}.bmp`} />
              </div>
              <label className="label1">{device.label}</label>
            </li>
          );
        })}
      </ul>
    );
  }

  renderSupportSystems() {
    return (
      <ul style={this.getLevel1Style("support-system")}>
        <li style={this.getLevel1Style("support-system")}>
          <input type="text" placeholder="Search" className="device-bar-search-input" ref={this.searchSupportRef} onKeyUp={e => this.handleSearch(e, "Support")} />
          <input type="button" className="device-bar-search-button" value="Clear" onClick={e => this.handleDeviceBarClear()} />
        </li>
        {this.state.supportSystemDevices.map((grp, i) => {
          return (
            <div key={i}>
              <ExpandedListItem
                title={grp.groupName}
                searchPattern={this.state.supportSearchPattern}
                ulClassName="">
                {grp.devices.map((device, j) => {
                  const selectedClassName =
                    this.isNonCustomDeviceSelected() &&
                    device.id === this.props.selectedDevice.deviceId
                      ? "selected-device"
                      : "";
                  return (
                    <li
                      id={device.id}
                      key={j}
                      className={selectedClassName}
                      onClick={_ => this.handleDeviceClicked(device.id, "SS")}
                      onContextMenu={e => this.deviceRightClick(e, "SS", device.id)}
                    >
                      <div
                        className={device.id}
                        draggable
                      >
                        <img src={`/HTMLImages/SSS/${device.id}.bmp`} draggable />
                      </div>
                      <label className="label1">{device.label}</label>
                    </li>
                  );
                })}
              </ExpandedListItem>
            </div>
          );
        })}
      </ul>
    );
  }

  render() {
    const inputDevices = this.state.devices.inputDevices
      .reduce(
        (groups, device) => {
          if (!groups[device.inputGroup]) {
            groups[device.inputGroup] = [];
          }
          groups[device.inputGroup].push(device);
          return groups;
        },
        {} as { [inputGroup: string]: DM_DataSheet[] }
      );

    const actionDevices = this.state.devices.actionDevices
      .reduce(
        (groups, device) => {
          if (!groups[device.actionGroup]) {
            groups[device.actionGroup] = [];
          }
          groups[device.actionGroup].push(device);
          return groups;
        },
        {} as { [actionGroup: string]: DM_DataSheet[] }
      );

    const srs: Array<string> = [
      "Process Hazard",
      "Description",
      "Diagnostics",
      "Reset",
      "Shutdown",
      "Reference",
      "Comments"
    ];

    return (
      <div>
        {/*Warning Modal if user tries to delete custom device that is used in a protective function*/}
        {this.props.customDeviceUsageInfo != null && this.props.customDeviceUsageInfo.function != null ? (
          <SilModal
            title="Silsolver"
            closeModal={this.closeModal}
            width={300}
          >
            <p>
              Can't delete custom {this.props.customDeviceUsageInfo.deviceType}. It is used in the following protective
              function(s): {this.props.customDeviceUsageInfo.function}
            </p>
            <div className="ui-dialog-buttonpane ui-widget-content ui-helper-clearfix">
              <div className="ui-dialog-buttonset">
                <button type="button" onClick={this.closeModal}>OK</button>
              </div>
            </div>
          </SilModal>
        ) : null}
        {/*Confirmation Modal if user tries to delete custom data sheet*/}
        {this.state.shownModal === "ConfirmDeletingCustomDataSheet"
          ? <ConfirmDeletingCustomDataSheetModal
              onClose={this.closeModal}
              onSubmit={this.confirmDeletingCustomDataSheetModelonSubmit}
              typeName={this.state.confirmDeletingCustomDataSheetTypeName}
            />
          : null}
        {/*Context modal*/}
        {this.state.shownModal === "Context Menu"
          ? <ContextMenu
            closeModal={this.closeModal}
            deviceMenuProps={this.state.contextMenuProps}
          />
          : null}
        {/*SRS Modals*/}
        {this.state.shownModal != null &&
        srs.indexOf(this.state.shownModal) > -1 ? (
          <SrsModal
            closeModal={this.closeModal}
            handleProcessHazardUpdate={this.props.handleProcessHazardUpdate}
            shownModal={this.state.shownModal}
            saveSrsNotes={this.props.saveSrsNotes}
            psf_ProtectiveFunctionComments={
              this.props.psf_ProtectiveFunctionComments
            }
          />
        ) : null}
        {/*Logic Solver Modal */}
        {this.state.shownModal === "Logic Solver Modal" ? (
          <LogicModal
            closeModal={this.closeModal}
            deviceSheet={this.state.rightClickedLogicDevice.logicSolverSheet}
            readOnly={true}
          />
        ) : null}
        {/*Device Modal*/}
        {this.state.shownModal === "Device Modal" ? (
          <DeviceModal
            closeModal={this.closeModal}
            dm_dataSheet={this.state.rightClickedDevice.dataSheet}
            readOnly={true}
          />
        ) : null}
        {/* Support System Modal */}
        {this.state.shownModal === "Support System Modal" ? (
          <SupportModal
            closeModal={this.closeModal}
            deviceSheet={this.state.rightClickedSupportDevice.supportSystemSheet}
            readOnly={true}
          />
          ) : null
        }
        {/*Customer Device Modal*/}
        {this.state.shownModal != null && this.state.shownModal === "Custom Device Modal" ? (
          <CustomerDeviceModal
            closeModal={this.closeModal}
            shownModal={this.state.shownModal}
            deviceSheet={
              this.state.rightClickedDevice != null
                ? this.state.rightClickedDevice
                : null
            }
            saveCustomDeviceSheet={this.props.saveCustomDeviceSheet}
          />
        ) : null}
        {/*Customer Logic Modal*/}
        {this.state.shownModal != null &&
        this.state.shownModal === "Custom Logic Solver Modal" ? (
          <CustomerLogicModal
            closeModal={this.closeModal}
            shownModal={this.state.shownModal}
            logicSheet={
              this.state.rightClickedLogicDevice != null
                ? this.state.rightClickedLogicDevice.logicSolverSheet
                : null
            }
            saveCustomLogicSolver={this.props.saveCustomLogicSolver}
          />
        ) : null}
        {/*Customer Support Modal*/}
        {this.state.shownModal != null &&
        this.state.shownModal === "Custom Support System Modal" ? (
          <CustomerSupportModal
            closeModal={this.closeModal}
            shownModal={this.state.shownModal}
            supportSheet={
              this.state.rightClickedSupportDevice != null
                ? this.state.rightClickedSupportDevice.supportSystemSheet
                : null
            }
            saveCustomSupportSystem={this.props.saveCustomSupportSystem}
          />
        ) : null}
        {/*Customer Device Right-Click Menu Items*/}
        {this.state.shownModal != null &&
        this.state.shownModal === "RightClick" ? (
          <CustomerDeviceGreenRightClickMenuItems
            closeModal={this.closeModal}
            deviceMenuX={this.state.customerDeviceRightClickMenuX}
            deviceMenuY={this.state.customerDeviceRightClickMenuY}
            openCustomerDeviceModal={this.openCustomerDeviceModal}
            rightClickedDevice={this.state.rightClickedDevice}
            rightClickedLogicDevice={this.state.rightClickedLogicDevice}
            rightClickedSupportDevice={this.state.rightClickedSupportDevice}
            openConfirmDeletingCustomDataSheetModel={this.openConfirmDeletingCustomDataSheetModel}
            promoteToClientLevel={(deviceType, sheetId) => this.promoteToClientLevel(deviceType, sheetId)}
            customDeviceRight={this.props.userDetails?.profile?.createCustomDatasheet ?? false}
          />
        ) : null}
        {/*Device Bar Device Right-Click Menu Items*/}
        {this.state.shownModal != null &&
        this.state.shownModal === "deviceBarDeviceMenuItems" ? (
          <DeviceBarDeviceMenuItems
            closeModal={this.closeModal}
            deviceBarDeviceMenuProps={this.state.deviceBarDeviceMenuProps}
          />
        ) : null}
        <ul className="menu_nav">
          <li onClick={e => this.handleLevelOneClick(e, "srs")}>SRS</li>
          <ul className="srs" style={this.getLevel1Style("srs")}>
            <li
              className="process"
              onClick={() => this.handleClick("Process Hazard")}
            >
              Process Hazard
            </li>
            <li
              className="process"
              onClick={() => this.handleClick("Description")}
            >
              Description
            </li>
            <li
              className="process"
              onClick={() => this.handleClick("Diagnostics")}
            >
              Diagnostics
            </li>
            <li className="process" onClick={() => this.handleClick("Reset")}>
              Reset
            </li>
            <li
              className="process"
              onClick={() => this.handleClick("Shutdown")}
            >
              Shutdown
            </li>
            <li
              className="process"
              onClick={() => this.handleClick("Reference")}
            >
              Reference
            </li>
            <li
              className="process"
              onClick={() => this.handleClick("Comments")}
            >
              Comments
            </li>
          </ul>

          <li id="logicSolverDeviceBar" onClick={e => this.handleLevelOneClick(e, "logic-solver")}>
            Logic Solver
          </li>
          {this.renderLogicSolvers()}

          <li id="inputConfigurationDeviceBar" onClick={e => this.handleLevelOneClick(e, "input-configuration")}>
            Input Configuration
          </li>
          <ul
            className="inputtoggle"
            style={this.getLevel1Style("input-configuration")}
          >
            <ExpandedListItem title="One device" ulClassName="Onedevice" searchPattern={null} >
              <li
                onClick={e =>
                  this.props.onConfigurationClick(
                    "Device1oo1",
                    ConfigurationType.Input
                  )
                }
              >
                <label>1oo1</label>
                <div id="Device1oo1" className="Device1oo1">
                  <img src="/HTMLImages/Image/Device1oo1.png" />
                </div>
              </li>
            </ExpandedListItem>

            <ExpandedListItem title="Two devices" ulClassName="twodevice" searchPattern={null}>
              <li
                onClick={_ =>
                  this.props.onConfigurationClick(
                    "Device1oo2",
                    ConfigurationType.Input
                  )
                }
              >
                <label>1oo2</label>
                <div id="Device1oo2" className="Device1oo2">
                  <img src="/HTMLImages/Image/Device1oo2.png" />
                </div>
              </li>
              <li
                onClick={_ =>
                  this.props.onConfigurationClick(
                    "Device2oo2",
                    ConfigurationType.Input
                  )
                }
              >
                <label>2oo2</label>
                <div id="Device2oo2" className="Device2oo2">
                  <img src="/HTMLImages/Image/Device2oo2.png" />
                </div>
              </li>
            </ExpandedListItem>
            <ExpandedListItem title="Three devices" ulClassName="threedevice" searchPattern={null}>
              <li
                onClick={e =>
                  this.props.onConfigurationClick(
                    "Device1oo3",
                    ConfigurationType.Input
                  )
                }
              >
                <label>1oo3</label>
                <div id="Device1oo3" className="Device1oo3">
                  <img src="/HTMLImages/Image/Device1oo3.png" />
                </div>
              </li>
              <li
                onClick={e =>
                  this.props.onConfigurationClick(
                    "Device2oo3",
                    ConfigurationType.Input
                  )
                }
              >
                <label>2oo3</label>
                <div id="Device2oo3" className="Device2oo3">
                  <img src="/HTMLImages/Image/Device2oo3.png" />
                </div>
              </li>
              <li
                onClick={e =>
                  this.props.onConfigurationClick(
                    "Device3oo3",
                    ConfigurationType.Input
                  )
                }
              >
                <label>3oo3</label>
                <div id="Device3oo3" className="Device3oo3">
                  <img src="/HTMLImages/Image/Device3oo3.png" />
                </div>
              </li>
            </ExpandedListItem>
            <ExpandedListItem title="Complex Voting" ulClassName="complex" searchPattern={null}>
              <li
                onClick={e =>
                  this.props.onConfigurationClick(
                    "1oo1",
                    ConfigurationType.Input
                  )
                }
              >
                <label>1oo1</label>
                <div id="Complex1oo1" className="Complex1oo1">
                  <img src="/HTMLImages/Image/1oo1.png" />
                </div>
              </li>
              <li
                onClick={e =>
                  this.props.onConfigurationClick(
                    "1oo2",
                    ConfigurationType.Input
                  )
                }
              >
                <label>1oo2</label>
                <div id="Complex1oo2" className="Complex1oo2">
                  <img src="/HTMLImages/Image/1oo2.png" />
                </div>
              </li>
              <li
                onClick={e =>
                  this.props.onConfigurationClick(
                    "2oo2",
                    ConfigurationType.Input
                  )
                }
              >
                <label>2oo2</label>
                <div id="Complex2oo2" className="Complex2oo2">
                  <img src="/HTMLImages/Image/2oo2.png" />
                </div>
              </li>
              <li
                onClick={e =>
                  this.props.onConfigurationClick(
                    "1oo3",
                    ConfigurationType.Input
                  )
                }
              >
                <label>1oo3</label>
                <div id="Complex1oo3" className="Complex1oo3">
                  <img src="/HTMLImages/Image/1oo3.png" />
                </div>
              </li>
              <li
                onClick={e =>
                  this.props.onConfigurationClick(
                    "2oo3",
                    ConfigurationType.Input
                  )
                }
              >
                <label>2oo3</label>
                <div id="Complex2oo3" className="Complex2oo3">
                  <img src="/HTMLImages/Image/2oo3.png" />
                </div>
              </li>
              <li
                onClick={e =>
                  this.props.onConfigurationClick(
                    "3oo3",
                    ConfigurationType.Input
                  )
                }
              >
                <label>3oo3</label>
                <div id="Complex3oo3" className="Complex3oo3">
                  <img src="/HTMLImages/Image/3oo3.png" />
                </div>
              </li>
              <li
                onClick={e =>
                  this.props.onConfigurationClick(
                    "MooN1oo1",
                    ConfigurationType.Input
                  )
                }
              >
                <label>MooN</label>
                <div id="ComplexMooN" className="ComplexMooN">
                  <img src="/HTMLImages/Image/MooNInputConfig.png" />
                </div>
              </li>
            </ExpandedListItem>
          </ul>

          <li id="inputDeviceDeviceBar" onClick={e => this.handleLevelOneClick(e, "input-device")}>
            Input Device
          </li>
          {this.renderInputDevices(inputDevices)}

          <li
            id="actionConfigurationDeviceBar"
            onClick={e => this.handleLevelOneClick(e, "action-configuration")}
          >
            Action Configuration
          </li>
          <ul
            className="actiontoggle"
            style={this.getLevel1Style("action-configuration")}
          >
            <ExpandedListItem
              title="Simplex Action"
              ulClassName="simplexaction"
              searchPattern={null}
            >
              <li
                className="action"
                onClick={e =>
                  this.props.onConfigurationClick(
                    "Simplex",
                    ConfigurationType.Action
                  )
                }
              >
                <label>1oo1</label>
                <div className="Simplex">
                  <img src="/HTMLImages/Image/Simplex.png" />
                </div>
              </li>
            </ExpandedListItem>
            <ExpandedListItem
              title="Duplicated Action"
              ulClassName="duplexaction"
              searchPattern={null}
            >
              <li
                className="action"
                onClick={e =>
                  this.props.onConfigurationClick(
                    "Duplicated",
                    ConfigurationType.Action
                  )
                }
              >
                <label>1oo2</label>
                <div className="Duplex">
                  <img src="/HTMLImages/Image/Duplicated.png" />
                </div>
              </li>
            </ExpandedListItem>
            <ExpandedListItem
              title="Triplicated Action"
              ulClassName="tripleaction"
              searchPattern={null}
            >
              <li
                className="action"
                onClick={e =>
                  this.props.onConfigurationClick(
                    "Triplex",
                    ConfigurationType.Action
                  )
                }
              >
                <label>1oo3</label>
                <div className="Triplex">
                  <img src="/HTMLImages/Image/Triplex.png" />
                </div>
              </li>
            </ExpandedListItem>
          </ul>

          <li id="actionDeviceDeviceBar" onClick={e => this.handleLevelOneClick(e, "action-device")}>
            Action Device
          </li>
          {this.renderActionDevices(actionDevices)}

          <li
            onClick={e => this.handleLevelOneClick(e, "support-configuration")}
          >
            Support Configuration
          </li>
          <ul
            className="supportconfig"
            style={this.getLevel1Style("support-configuration")}
          >
            <li onClick={e => this.props.onSupportConfigurationClick()}>
              <div className="arc">
                <img src="/HTMLImages/Elements/SupportConfig.png" />
              </div>
            </li>
          </ul>

          <li onClick={e => this.handleLevelOneClick(e, "support-system")}>
            Support System
          </li>
          {this.renderSupportSystems()}

          <li onClick={e => this.handleLevelOneClick(e, "custom-device")}>
            Customer Device
          </li>
          <ul className="customer" style={this.getLevel1Style("custom-device")}>
            <li style={this.getLevel1Style("custom-device")}>
              <input type="text" placeholder="Search" className="device-bar-search-input" ref={this.searchCustomRef} onKeyUp={e => this.handleSearch(e, "Custom")} />
              <input type="button" className="device-bar-search-button" value="Clear" onClick={e => this.handleDeviceBarClear()} />
            </li>
            <li
              className="customerdevice"
              style={{ fontWeight: "bold" }}
              onClick={() => this.isCustomDataSheetAllowed() ? this.handleClick("Custom Device Modal") : null}
            >
              Device
            </li>
            {/*Need to pass in data_sheet below in on ContextMenu*/}
            <li className="customer_add_device" style={{ paddingTop: "0px" }}>
              {this.state.devices.customDevices.map((deviceSheetBar, i) => {
                const dataSheet = deviceSheetBar.dataSheet;
                const deviceSheetBarClose = deviceSheetBar;
                const selectedClassName =
                  this.props.selectedDevice &&
                  dataSheet.dataSheetId === this.props.selectedDevice.deviceDataSheetId
                    ? "selected-device"
                    : "";
                const labelStyle: React.CSSProperties = {
                  fontWeight: deviceSheetBarClose.isClient ? "bold" : "normal"
                };
                const imgStyle: React.CSSProperties = {
                  border: deviceSheetBarClose.isClient ? "5px solid green": "none"
                }
                return (
                  <div
                    className={selectedClassName}
                    key={i}
                    onClick={_ =>
                      this.handleDeviceClicked(
                        dataSheet.deviceId,
                        "DEVICE",
                        dataSheet.dataSheetId
                      )
                    }
                  >
                    <img style={imgStyle}
                      src={`/HTMLImages/Device/Cus/${dataSheet.deviceId}.bmp`}
                      onContextMenu={(e: React.MouseEvent) =>
                        this.handleCustomerDeviceRightClick(e, "RightClick", deviceSheetBarClose)
                      }
                    />
                    <label style={labelStyle} className="label1">{dataSheet.deviceDescription}</label>
                  </div>
                );
              })}
            </li>
            <li
              className="customerlogic"
              style={{ fontWeight: "bold" }}
              onClick={() => this.isCustomDataSheetAllowed() ? this.handleClick("Custom Logic Solver Modal") : null}
            >
              Logic Solver
            </li>
            <li
              className="customer_add_logic"
              style={{
                paddingTop: "0px",
                backgroundColor: "rgb(184, 184, 184)"
              }}
            >
              {this.state.devices.customLogicSolvers.map((cls, i) => {
                const selectedClassName =
                  this.props.selectedDevice &&
                    cls.logicSolverSheet.logicSolverSheetId ===
                    this.props.selectedDevice.logicSolverSheetId
                    ? "selected-device"
                    : "";
                const labelStyle: React.CSSProperties = {
                  fontWeight: cls.isClient ? "bold" : "normal"
                }
                const imgStyle: React.CSSProperties = {
                  border: cls.isClient ? "5px solid green" : "none"
                }
                return (
                  <div
                    className={selectedClassName}
                    key={i}
                    onClick={_ =>
                      this.handleDeviceClicked(
                        cls.logicSolverSheet.deviceID,
                        "LS",
                        cls.logicSolverSheet.logicSolverSheetId
                      )
                    }
                  >
                    <img
                      style={imgStyle}
                      src={`/HTMLImages/LSS/Cus/${cls.logicSolverSheet.deviceID}.bmp`}
                      onContextMenu={(e: React.MouseEvent) =>
                        this.handleCustomerLogicDeviceRightClick(
                          e,
                          "RightClick",
                          cls
                        )
                      }
                    />
                    <label style={labelStyle} className="label1">{cls.logicSolverSheet.deviceType}</label>
                  </div>
                );
              })}
            </li>
            <li className="customer_add_logic" style={{ paddingTop: "0px" }} />
            <li
              className="customersupport"
              style={{ fontWeight: "bold" }}
              onClick={() => this.isCustomDataSheetAllowed() ? this.handleClick("Custom Support System Modal") : null}
            >
              Support System
            </li>
            {/*Need to pass in support_sheet below in on ContextMenu*/}
            <li className="customer_add_support" style={{ paddingTop: "0px" }}>
              {this.state.devices.customSupportSystems.map((css, i) => {
                const selectedClassName =
                  this.props.selectedDevice &&
                    css.supportSystemSheet.supportSystemSheetId ===
                    this.props.selectedDevice.supportSheetId
                    ? "selected-device"
                    : "";
                const labelStyle: React.CSSProperties = {
                  fontWeight: css.isClient ? "bold" : "normal"
                };
                const imgStyle: React.CSSProperties = {
                  border: css.isClient ? "5px solid green" : "none"
                }
                return (
                  <div
                    key={i}
                    className={selectedClassName}
                    onClick={_ =>
                      this.handleDeviceClicked(
                        css.supportSystemSheet.supportSystemID,
                        "SS",
                        css.supportSystemSheet.supportSystemSheetId
                      )
                    }
                  >
                    <img
                      style={imgStyle}
                      src={`/HTMLImages/SSS/Cus/${css.supportSystemSheet.supportSystemID}.bmp`}
                      onContextMenu={(e: React.MouseEvent) =>
                        this.handleCustomerSupportDeviceRightClick(
                          e,
                          "RightClick",
                          css
                        )
                      }
                    />
                    <label style={labelStyle} className="label1">{css.supportSystemSheet.supportSystemType}</label>
                  </div>
                );
              })}
            </li>
          </ul>
          <li onClick={e => this.handleLevelOneClick(e, "manual-device")}>
            Manual Device
          </li>
          <ul
            className="manualDevice"
            style={this.getLevel1Style("manual-device")}
          >
            <li
              className="manualInput"
              style={{ fontWeight: "bold" }}
              onClick={this.props.createManualInput}
            >
              Manual Input
            </li>
            <li
              className="manualAction"
              style={{ fontWeight: "bold" }}
              onClick={this.props.createManualAction}
            >
              Manual Action
            </li>
          </ul>
        </ul>
      </div>
    );
  }
}
